import React, {useState} from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    FormControlLabel,
    Radio,
    Checkbox,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    SmartToy as TutorIcon,
} from '@mui/icons-material';

// Helper function to format score nicely
const formatScore = (score) => {
    if (score % 1 === 0) {
        // If it's a whole number, return as integer
        return score.toString();
    } else if (score % 0.5 === 0) {
        // If it's a half number (like 1.5), show one decimal
        return score.toFixed(1);
    } else {
        // For other decimals, round to 2 decimal places
        return (Math.round(score * 100) / 100).toString();
    }
};

const DetailedQuestionResult = ({
                                    answer,
                                    question,
                                    onChatWithTutor,
                                    accessToken
                                }) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandChange = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    const renderUserAnswer = () => {
        const userAnswer = answer.value || answer.value_text || answer.value_json;

        if (question.type === 'single_choice' || question.type === 'multi_choice') {
            const correctAnswers = Array.isArray(question.config?.correct_answer)
                ? question.config.correct_answer
                : [question.config?.correct_answer];

            let userSelectedValues = [];
            if (typeof userAnswer === 'string') {
                userSelectedValues = [userAnswer];
            } else if (Array.isArray(userAnswer)) {
                userSelectedValues = userAnswer;
            }

            const isMultiChoice = question.type === 'multi_choice';

            return (
                <Box sx={{mt: 2}}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1, fontWeight: 'bold'}}>
                        Your Answer:
                    </Typography>
                    {question.options?.map(option => {
                        const isSelected = userSelectedValues.includes(option.value);
                        const isCorrect = correctAnswers.includes(option.value);

                        let borderColor = (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'grey.300';
                        let backgroundColor = 'transparent';
                        let textColor = 'text.primary';

                        if (isSelected) {
                            if (isCorrect) {
                                borderColor = 'success.main';
                                backgroundColor = 'transparent';
                                textColor = 'success.dark';
                            } else {
                                borderColor = 'error.main';
                                backgroundColor = 'transparent';
                                textColor = 'error.dark';
                            }
                        } else if (isCorrect && !isSelected) {
                            // Show correct answers that weren't selected (for multi-choice)
                            borderColor = 'success.main';
                            backgroundColor = 'transparent';
                            textColor = 'success.dark';
                        }

                        return (
                            <Box key={option.value} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: (theme) => {
                                    if (backgroundColor === 'transparent') {
                                        return theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'transparent';
                                    }
                                    return backgroundColor;
                                },
                                border: isSelected || (isCorrect && !isSelected) ? 1 : 0,
                                borderColor,
                                opacity: isSelected || (isCorrect && !isSelected) ? 1 : 0.7
                            }}>
                                <FormControlLabel
                                    control={
                                        isMultiChoice ? (
                                            <Checkbox
                                                checked={isSelected}
                                                color={isSelected && isCorrect ? 'success' : 'error'}
                                                disabled
                                            />
                                        ) : (
                                            <Radio
                                                checked={isSelected}
                                                color={isSelected && isCorrect ? 'success' : 'error'}
                                                disabled
                                            />
                                        )
                                    }
                                    label={
                                        <Typography variant="body2" sx={{
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            color: textColor
                                        }}>
                                            {option.label || option.text}
                                        </Typography>
                                    }
                                    sx={{flex: 1, margin: 0}}
                                />
                                {isSelected && (
                                    <Typography sx={{ml: 1, fontSize: '1.2em'}}>
                                        {isCorrect ? '✅' : '❌'}
                                    </Typography>
                                )}
                                {!isSelected && isCorrect && (
                                    <Typography sx={{ml: 1, fontSize: '1.2em'}}>
                                        💡
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            );
        }

        // For text/number/string questions - show user answer and correct answer if wrong
        return (
            <Box sx={{mt: 2}}>
                <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1, fontWeight: 'bold'}}>
                    Deine Antwort:
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: answer.is_correct ? 'success.main' : 'error.main',
                        fontWeight: 'medium',
                        whiteSpace: 'pre-wrap',
                        backgroundColor: (theme) => answer.is_correct
                            ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)')
                            : (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.08)'),
                        p: 1.5,
                        borderRadius: 1,
                        border: 1,
                        borderColor: answer.is_correct ? 'success.main' : 'error.main',
                        mb: !answer.is_correct && question.config?.correct_answer ? 2 : 0
                    }}
                >
                    {userAnswer || 'No answer provided'}
                </Typography>

                {/* Show correct answer if the user was wrong and there is a correct answer */}
                {!answer.is_correct && question.config?.correct_answer && (
                    <Box sx={{mt: 2}}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1, fontWeight: 'medium'}}>
                            Correct Answer:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'success.main',
                                fontWeight: 'medium',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(76, 175, 80, 0.05)'
                                    : 'transparent',
                                p: 1,
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'success.light',
                                borderStyle: 'solid'
                            }}
                        >
                            {question.config.correct_answer}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const renderFeedback = () => {
        let feedbackText = '';
        let showFeedback = false;

        if (question.type === 'text') {
            feedbackText = answer.reasoning;
            showFeedback = !!answer.reasoning;
        } else {
            feedbackText = question.config?.explanation;
            showFeedback = !!question.config?.explanation;
        }

        if (!showFeedback) return null;

        return (
            <Box sx={{mt: 2}}>
                {/* Header row with Feedback label and Chat button */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{fontWeight: 'bold'}}>
                        Feedback:
                    </Typography>
                    {accessToken && onChatWithTutor && (
                        <Button
                            variant="contained"
                            startIcon={<TutorIcon/>}
                            onClick={() => onChatWithTutor(question, answer)}
                            size="small"
                            color="primary"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'medium',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 2px 4px rgba(0,0,0,0.3)'
                                    : '0 2px 4px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 4px 8px rgba(0,0,0,0.4)'
                                        : '0 4px 8px rgba(0,0,0,0.2)',
                                }
                            }}
                        >
                            Chat mit Tutor
                        </Button>
                    )}
                </Box>
                <Card variant="outlined" sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.02)'
                        : 'background.paper'
                }}>
                    <CardContent sx={{py: 2}}>
                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                            {feedbackText}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleExpandChange}
            sx={{
                mb: 2,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0,0,0,0.3)'
                    : '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: answer.is_correct ? 'success.light' : 'error.light',
                '&:before': {
                    display: 'none',
                },
                // Override MUI's default first/last child border radius
                '&:first-of-type': {
                    borderRadius: 1,
                },
                '&:last-of-type': {
                    borderRadius: 1,
                },
                // Ensure the accordion summary respects our border radius
                '& .MuiAccordionSummary-root': {
                    borderTopLeftRadius: 2,
                    borderTopRightRadius: 2,
                    borderBottomLeftRadius: expanded ? 0 : 2,
                    borderBottomRightRadius: expanded ? 0 : 2,
                },
                // Ensure the accordion details respects our border radius when expanded
                '& .MuiAccordionDetails-root': {
                    borderBottomLeftRadius: 2,
                    borderBottomRightRadius: 2,
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{
                    background: (theme) => answer.is_correct
                        ? (theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.25) 100%)'
                            : 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)')
                        : (theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.25) 100%)'
                            : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'),
                    borderLeft: 4,
                    borderLeftColor: answer.is_correct ? 'success.main' : 'error.main',
                    minHeight: 64,
                    '&:hover': {
                        background: (theme) => answer.is_correct
                            ? (theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.3) 100%)'
                                : 'linear-gradient(135deg, #d4e6d4 0%, #a5d6a7 100%)')
                            : (theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(244, 67, 54, 0.3) 100%)'
                                : 'linear-gradient(135deg, #ffcdd2 0%, #ef9a9a 100%)'),
                    },
                    '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                    }
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 2}}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: answer.is_correct ? 'success.main' : 'error.main',
                        color: 'white',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 2px 4px rgba(0,0,0,0.4)'
                            : '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                        {answer.is_correct ? (
                            <CheckIcon sx={{fontSize: 24}}/>
                        ) : (
                            <CancelIcon sx={{fontSize: 24}}/>
                        )}
                    </Box>
                    <Typography variant="h6" sx={{
                        fontWeight: 600,
                        flex: 1,
                        color: 'text.primary',
                        fontSize: '1.1rem'
                    }}>
                        {question.text || question.title}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {answer.points_earned !== undefined && (
                            <Chip
                                label={`${formatScore(answer.points_earned)}/${formatScore(answer.question?.points || answer.max_points || 1)} Pkt.`}
                                size="small"
                                color={answer.is_correct ? 'success' : 'error'}
                                variant="outlined"
                                sx={{fontWeight: 'medium'}}
                            />
                        )}
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'grey.50',
                padding: 3
            }}>
                <Box sx={{pt: 1}}>
                    {renderUserAnswer()}
                    {renderFeedback()}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default DetailedQuestionResult;